/**
 * WHICH NOTE THE VIEWER IS LOOKING AT (#14544).
 *
 * The note page's counterpart to `meeting-page-context.ts`, for the same
 * reason and in the same shape: the recording bar is mounted in the root
 * layout, above the router, so the page PUBLISHES which note is on screen and
 * the bar SUBSCRIBES. On `/notes/[id]` the bar's main Record then records
 * into that note rather than starting a new one.
 *
 * It is the answer to "what is on screen", not a cache of the note, and it
 * is cleared when the page unmounts: a stale answer would point Record at a
 * note the viewer navigated away from.
 *
 * ## Client-safe
 *
 * No `server-only`, no Prisma, no DOM. The publisher is a client component
 * rendered by the server page, which computes every field.
 */

export type NotePageContext = {
  noteId: string
  /** Drawn on the bar's main control, and names the capture once running. */
  title: string
  /**
   * May this viewer record INTO this note? Server-read with the note's write
   * gate (`canEditNoteBody`) and the recording start's own rule. `false`
   * leaves the bar exactly as it is on any other page.
   */
  canRecord: boolean
  /** Recordings the note already holds; the next one is part `n + 1`. */
  recordingCount: number
  /**
   * The note's calendar event or meeting has a video call this viewer can
   * see (#15666): recording into it defaults to the microphone plus
   * computer audio.
   */
  hasVirtualMeeting: boolean
}

function sameContext(left: NotePageContext, right: NotePageContext): boolean {
  // Field by field, for `meeting-page-context.ts`'s reason: every identity
  // change re-renders app-wide chrome through `useSyncExternalStore`.
  return (
    left.noteId === right.noteId &&
    left.title === right.title &&
    left.canRecord === right.canRecord &&
    left.recordingCount === right.recordingCount &&
    left.hasVirtualMeeting === right.hasVirtualMeeting
  )
}

let current: NotePageContext | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeToNotePageContext(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getNotePageContext(): NotePageContext | null {
  return current
}

/** Always `null`: a server render has no page mounted yet. */
export function getServerNotePageContext(): NotePageContext | null {
  return null
}

export function publishNotePageContext(next: NotePageContext): void {
  if (current !== null && sameContext(current, next)) return
  current = next
  emit()
}

/**
 * Clear the answer, but only if it is still this caller's: a note-to-note
 * navigation mounts the next page's publisher before the previous page's
 * cleanup runs.
 */
export function clearNotePageContext(noteId: string): void {
  if (current?.noteId !== noteId) return
  current = null
  emit()
}

/** Test-only: drop everything, so one case cannot leak into the next. */
export function resetNotePageContext(): void {
  current = null
  emit()
}
